// Updated Notifications parent component
import { useState, useEffect, useCallback, useContext } from "react";
import {
  Bell,
  Send,
  Calendar,
  Plus,
  Search,
  Eye,
  Clock,
  Users,
  MousePointer,
  Percent,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import StatCard from "../../components/StatCard";
import NotificationSendModal from "../../components/NotificationSendModal";
import ScheduledNotificationModal from "../../components/ScheduledNotificationModal";
import NotificationDetailModal from "../../components/NotificationDetailModal";
import Layout from "../../components/Layout/Layout";
import SidebarList from "../../components/Sidebar";
import { AuthContext } from "../../hoc/AuthContext";
import { convertToIst } from "../../lib/utils";

const baseUrl = process.env.REACT_APP_NOTIFICATION_BASE_URL;

export default function Notifications() {
  const { selectedDomain, authUserData } = useContext(AuthContext);
  let user = authUserData?.userData?.first_name || "user";

  const domainUrl = selectedDomain.domain_url;
  const [sentNotifications, setSentNotifications] = useState([]);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("sent");
  const [sentPage, setSentPage] = useState(1);
  const [scheduledPage, setScheduledPage] = useState(1);
  const [sentTotalPages, setSentTotalPages] = useState(1);
  const [scheduledTotalPages, setScheduledTotalPages] = useState(1);
  const [cancelingIds, setCancelingIds] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [topicsError, setTopicsError] = useState(null);
  const [stats, setStats] = useState([
    {
      name: "Total Subscribers",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: Users,
      iconBg: "bg-red-50",
      iconColor: "text-red-600",
    },
    {
      name: "Notifications Sent",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: Send,
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      name: "Total Clicks",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: MousePointer,
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
    },
    {
      name: "CTR",
      value: "0%",
      change: "0%",
      changeType: "neutral",
      icon: Percent,
      iconBg: "bg-orange-50",
      iconColor: "text-orange-600",
    },
  ]);
  const itemsPerPage = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [domainInfo, setDomainInfo] = useState(null);
  const [domainLoading, setDomainLoading] = useState(true);
  const [sendError, setSendError] = useState(null);

  // Fetch domain info
  useEffect(() => {
    const fetchDomain = async () => {
      setDomainLoading(true);
      setError(null);
      try {
        const response = await fetch(`${baseUrl}domain/domainId/${domainUrl}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch domain info");
        }

        const result = await response.json();
        if (result.status !== 200) {
          throw new Error(result.message || "API error");
        }

        setDomainInfo(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setDomainLoading(false);
      }
    };
    if (domainUrl) fetchDomain();
  }, [domainUrl]);

  const domainId = domainInfo?.id;

  const fetchAnalytics = useCallback(async () => {
    if (!domainId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${baseUrl}analytics/notification/record/${domainId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) throw new Error("Failed to fetch analytics");
      const result = await response.json();
      if (result.status !== 200) throw new Error(result.message || "API error");

      const {
        totalSubscribers,
        noitifactionCount,
        noitifactionClick,
        totalCtr,
      } = result.data;

      setStats([
        {
          name: "Total Subscribers",
          value: totalSubscribers.toLocaleString(),
          change: "",
          changeType: "neutral",
          icon: Users,
          iconBg: "bg-red-50",
          iconColor: "text-red-600",
        },
        {
          name: "Notifications Sent",
          value: noitifactionCount.toLocaleString(),
          change: "",
          changeType: "neutral",
          icon: Send,
          iconBg: "bg-green-50",
          iconColor: "text-green-600",
        },
        {
          name: "Total Clicks",
          value: noitifactionClick.toLocaleString(),
          change: "",
          changeType: "neutral",
          icon: MousePointer,
          iconBg: "bg-purple-50",
          iconColor: "text-purple-600",
        },
        {
          name: "CTR",
          value: `${totalCtr.toFixed(2)}%`,
          change: "",
          changeType: "neutral",
          icon: Percent,
          iconBg: "bg-orange-50",
          iconColor: "text-orange-600",
        },
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [domainId]);
  useEffect(() => {
    if (!domainUrl) return;

    const fetchTopics = async () => {
      setTopicsLoading(true);
      setTopicsError(null);
      try {
        const res = await fetch(
          `${baseUrl}topic/get-by-domain/${domainUrl}?page=1&limit=100`,
        );
        if (!res.ok) throw new Error("Failed to load topics");
        const json = await res.json();
        if (json.status !== 200) throw new Error(json.message ?? "API error");

        const list = json.data.topics.map((t) => ({
          id: t.id,
          name: t.name,
        }));
        setTopics(list);
      } catch (e) {
        setTopicsError(e.message);
      } finally {
        setTopicsLoading(false);
      }
    };
    if (domainUrl) fetchTopics();
  }, [domainUrl]);
  // Fetch analytics on domain load
  useEffect(() => {
    if (domainId) {
      fetchAnalytics();
    }
  }, [domainId, fetchAnalytics]);

  const fetchSentNotifications = async () => {
    if (!domainId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${baseUrl}notification/get-notification-list/${domainId}?page=${sentPage}&limit=${itemsPerPage}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch sent notifications");
      }

      const result = await response.json();
      if (result.status !== 200) {
        throw new Error(result.message || "API error");
      }
      const mappedNotifications = result.data.list.map((notification) => ({
        id: notification.id,
        title: notification.title,
        url: notification.url,
        campaign: notification.topicId[0] || "Unknown Campaign",
        sent: notification.totalUserSend,
        clicked: notification.clickCount,
        totalclicked:
          notification.clickCount + notification.bt1Clount ||
          0 + notification.bt2Clount ||
          0,
        closeCount: notification.closeCount,
        ctr:
          notification.totalUserSend > 0
            ? (
                (notification.clickCount / notification.totalUserSend) *
                100
              ).toFixed(2) + "%"
            : "0%",
        date: convertToIst(notification.createdAt),
        completedAt: notification.completedAt
          ? convertToIst(notification.completedAt)
          : "",
        utm_source: notification.utm_source || "",
        utm_medium: notification.utm_medium || "",
        utm_campaign: notification.utm_campaign || "",
        status: notification.status,
        createdBy: notification.createdBy,
        totalUserSend: notification.totalUserSend,
        totalUsers: notification.totalUsers,
        bt2Clount: notification.bt2Clount,
        bt1Clount: notification.bt1Clount,
        btn_list: notification.btn_list,
        schdeuleId: notification.schdeuleId,
        immpression: notification.immpression || 0,
        // closeCount: notification.closeCount,
      }));

      setSentNotifications(mappedNotifications);
      setSentTotalPages(Math.ceil(result.data.totalCount / itemsPerPage));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  function timestampToDate(timestamp) {
    const date = new Date(timestamp);
    const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based, add 1
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  }
  const fetchScheduledNotifications = async () => {
    if (!domainId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${baseUrl}notification/schedule/notification/list/${domainId}?page=${scheduledPage}&limit=${itemsPerPage}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch scheduled notifications");
      }

      const result = await response.json();
      if (result.status !== 200) {
        throw new Error(result.message || "API error");
      }

      const mappedNotifications = result.data.list.map((notification) => ({
        id: notification.id,
        title: notification.title,
        url: notification.url,
        campaign: notification.topicId[0] || "Unknown Campaign",
        schedule: notification.scheduledTime,
        dateRange: `${timestampToDate(
          notification.startDate,
        )}-${timestampToDate(notification.endDate)}`,
        status: notification.status.toLowerCase(),
        utm_source: notification.utm_source || "",
        utm_medium: notification.utm_medium || "",
        date: convertToIst(notification.createdAt),

        createdBy: notification.createdBy,
      }));

      setScheduledNotifications(mappedNotifications);
      setScheduledTotalPages(Math.ceil(result.data.totalCount / itemsPerPage));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "sent" && domainId) {
      fetchSentNotifications();
    }
  }, [activeTab, sentPage, domainId]);

  useEffect(() => {
    if (activeTab === "scheduled" && domainId) {
      fetchScheduledNotifications();
    }
  }, [activeTab, scheduledPage, itemsPerPage, domainId]);

  // Handle sending a new notification (actual API call)
  const handleSendNotification = async (modalData) => {
    if (!domainId) return;
    setSendError(null);
    try {
      const payload = {
        domainId,
        ...modalData,
        language: domainInfo.language, // Pass language to API
        createdBy: user,
      };
      let url = `${baseUrl}notification/send-notification`;
      if (domainInfo.domainId == "jagran.com") {
        url = `${baseUrl}notification/send-notification-batch`;
      }
   
      const response = await fetch(url, {
        // Adjust endpoint if different
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const result = await response.json();

        throw new Error(result?.error || "Failed to send notification");
      }

      const result = await response.json();

      if (result.status !== 200) {
        throw new Error(result.message || "API error");
      }
      setIsSendModalOpen(false);

      // Refresh data
      fetchSentNotifications();
      fetchAnalytics();
    } catch (err) {
      setSendError(err.message);
    }
  };

  const handleScheduleNotification = (notification) => {
    // Similar implementation for scheduling if needed
    // For now, placeholder as in original
    const newNotification = {
      id: Date.now(),
      ...notification,
      status: "pending",
    };
    setScheduledNotifications([newNotification, ...scheduledNotifications]);
    // Add actual schedule API call here if endpoint provided
  };

  const handleViewDetails = (notification) => {
    setSelectedNotification(notification);
    setIsDetailModalOpen(true);
  };

  const handleCancelScheduled = async (notificationId) => {
    if (!domainId || !notificationId) return;
    try {
      // mark as canceling to disable button
      setCancelingIds((prev) => [...prev, notificationId]);

      const payload = {
        domainId,
        id: notificationId,
      };

      const res = await fetch(`${baseUrl}notification/cancelNotification`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(
          (json && json.message) || "Failed to cancel notification",
        );
      }

      const result = await res.json().catch(() => null);
      if (result && result.status && result.status !== 200) {
        throw new Error(result.message || "API error");
      }

      // refresh scheduled list and analytics
      await fetchScheduledNotifications();
      fetchAnalytics();
    } catch (err) {
      // keep it simple: set error state so user can see it in UI
      setError(err.message || String(err));
    } finally {
      setCancelingIds((prev) => prev.filter((id) => id !== notificationId));
    }
  };

  const filteredSentNotifications = sentNotifications.filter((n) =>
    n.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredScheduledNotifications = scheduledNotifications.filter((n) =>
    n.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || "";
    switch (statusLower) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "ongoing":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "scheduled":
        return "bg-purple-100 text-purple-800";
      case "active":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (domainLoading) {
    return (
      <Layout bodynew={true}>
        <div className="flex min-h-screen w-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Loading domain info...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout bodynew={true}>
      <div className="flex min-h-screen w-full">
        <SidebarList />
        <main className="flex-1 overflow-auto">
          <div className="p-6 space-y-8">
            {/* Header */}
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Notifications
              </h1>
              <p className="text-muted-foreground mt-1">
                Send and schedule push notifications
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <StatCard key={stat.name} {...stat} />
              ))}
            </div>

            {/* Error */}
            {error && <div className="text-red-600 text-center">{error}</div>}

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="space-y-6"
            >
              <div className="flex flex-col lg:flex-row gap-4 justify-between">
                <TabsList>
                  <TabsTrigger value="sent" className="gap-2">
                    <Send className="h-4 w-4" />
                    Send Now
                  </TabsTrigger>
                  <TabsTrigger value="scheduled" className="gap-2">
                    <Calendar className="h-4 w-4" />
                    Scheduled
                  </TabsTrigger>
                </TabsList>

                <div className="flex gap-3">
                  {/* <div className="relative flex-1 lg:flex-initial">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search notifications..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-full lg:w-64"
                      disabled={isLoading}
                    />
                  </div> */}
                  {activeTab === "sent" ? (
                    <Button
                      onClick={() => setIsSendModalOpen(true)}
                      disabled={isLoading}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Send Notification
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setIsScheduleModalOpen(true)}
                      disabled={isLoading}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Schedule Notification
                    </Button>
                  )}
                </div>
              </div>

              {/* Sent Notifications */}
              <TabsContent value="sent" className="space-y-4">
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    {isLoading ? (
                      <div className="flex justify-center items-center py-10">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="ml-2 text-muted-foreground">
                          Loading notifications...
                        </span>
                      </div>
                    ) : (
                      <>
                        <table className="w-full">
                          <thead className="bg-secondary/50">
                            <tr>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                                Notification
                              </th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                                Status
                              </th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                                Sent
                              </th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                                Received(est*)
                              </th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                                Clicked
                              </th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                                Closed
                              </th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                                CTR
                              </th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {filteredSentNotifications.length > 0 ? (
                              filteredSentNotifications.map((notification) => (
                                <tr
                                  key={notification.id}
                                  className="hover:bg-secondary/30 transition-colors"
                                >
                                  <td className="px-6 py-4">
                                    <div>
                                      <p className="font-medium text-foreground">
                                        {notification.title}
                                      </p>
                                      <p className="text-sm text-muted-foreground">
                                        createdBy: {notification.createdBy} •{" "}
                                        {notification.date}{" "}
                                        {notification?.schdeuleId && (
                                          <span
                                            className={`inline-flex ml-2 items-center text-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                              notification.status,
                                            )}`}
                                          >
                                            schdeule
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm text-foreground">
                                    <span
                                      className={`inline-flex items-center text-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                        notification.status,
                                      )}`}
                                    >
                                      {notification.status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm text-foreground">
                                    {notification.sent.toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm text-foreground">
                                    {notification.immpression.toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm text-foreground">
                                    {notification.totalclicked.toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm text-foreground">
                                    {notification.closeCount.toLocaleString()}
                                  </td>
                                  <td className="px-6 py-4 text-right text-sm font-medium text-foreground">
                                    {notification.ctr}
                                  </td>
                                  <td className="px-6 py-4 text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleViewDetails(notification)
                                      }
                                      className="text-primary hover:text-primary"
                                      disabled={isLoading}
                                    >
                                      <Eye className="h-4 w-4 mr-1" />
                                      View
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="px-6 py-12 text-center"
                                >
                                  <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                                  <p className="text-muted-foreground">
                                    No sent notifications found
                                  </p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {!isLoading && (
                          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                            <p className="text-sm text-muted-foreground">
                              Showing {(sentPage - 1) * itemsPerPage + 1} to{" "}
                              {Math.min(
                                sentPage * itemsPerPage,
                                filteredSentNotifications.length,
                              )}{" "}
                              of {filteredSentNotifications.length}{" "}
                              Notifications
                            </p>

                            <div className="flex items-center gap-2">
                              {/* Previous Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setSentPage((p) => Math.max(1, p - 1))
                                }
                                disabled={sentPage === 1 || isLoading}
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>

                              {/* Page Numbers */}
                              <div className="flex items-center gap-1">
                                {/* First Page */}
                                {sentTotalPages > 1 && (
                                  <Button
                                    variant={
                                      sentPage === 1 ? "default" : "outline"
                                    }
                                    size="sm"
                                    onClick={() => setSentPage(1)}
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    1
                                  </Button>
                                )}

                                {/* Ellipsis before */}
                                {sentPage > 4 && (
                                  <span className="px-2 text-muted-foreground">
                                    ...
                                  </span>
                                )}

                                {/* 2 pages before */}
                                {sentPage > 3 &&
                                  Array.from(
                                    { length: 2 },
                                    (_, i) => sentPage - 3 + i,
                                  )
                                    .filter((p) => p > 1)
                                    .map((page) => (
                                      <Button
                                        key={page}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setSentPage(page)}
                                        className="w-10"
                                        disabled={isLoading}
                                      >
                                        {page}
                                      </Button>
                                    ))}

                                {/* 1 page before */}
                                {sentPage > 2 && sentPage - 2 > 1 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSentPage(sentPage - 2)}
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    {sentPage - 2}
                                  </Button>
                                )}

                                {/* 1 page before current */}
                                {sentPage > 1 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSentPage(sentPage - 1)}
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    {sentPage - 1}
                                  </Button>
                                )}

                                {/* Current Page */}
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="w-10"
                                  disabled
                                >
                                  {sentPage}
                                </Button>

                                {/* 1 page after */}
                                {sentPage < sentTotalPages && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSentPage(sentPage + 1)}
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    {sentPage + 1}
                                  </Button>
                                )}

                                {/* 2 pages after */}
                                {sentPage < sentTotalPages - 1 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSentPage(sentPage + 2)}
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    {sentPage + 2}
                                  </Button>
                                )}

                                {/* Ellipsis after */}
                                {sentPage < sentTotalPages - 3 && (
                                  <span className="px-2 text-muted-foreground">
                                    ...
                                  </span>
                                )}

                                {/* Last Page */}
                                {sentTotalPages > 1 &&
                                  sentPage !== sentTotalPages && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        setSentPage(sentTotalPages)
                                      }
                                      className="w-10"
                                      disabled={isLoading}
                                    >
                                      {sentTotalPages}
                                    </Button>
                                  )}
                              </div>

                              {/* Next Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setSentPage((p) =>
                                    Math.min(sentTotalPages, p + 1),
                                  )
                                }
                                disabled={
                                  sentPage === sentTotalPages || isLoading
                                }
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Scheduled Notifications */}
              <TabsContent value="scheduled" className="space-y-4">
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    {isLoading ? (
                      <div className="flex justify-center items-center py-10">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="ml-2 text-muted-foreground">
                          Loading notifications...
                        </span>
                      </div>
                    ) : (
                      <>
                        <table className="w-full">
                          <thead className="bg-secondary/50">
                            <tr>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                                Notification
                              </th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                                Schedule
                              </th>
                              <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">
                                Date Range
                              </th>
                              <th className="px-6 py-4 text-center text-sm font-semibold text-foreground">
                                Status
                              </th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                                Cancel
                              </th>
                              <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {filteredScheduledNotifications.length > 0 ? (
                              filteredScheduledNotifications.map(
                                (notification) => (
                                  <tr
                                    key={notification.id}
                                    className="hover:bg-secondary/30 transition-colors"
                                  >
                                    <td className="px-6 py-4">
                                      <div>
                                        <p className="font-medium text-foreground">
                                          {notification.title}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                          createdBy: {notification.createdBy} •{" "}
                                          {notification.date}
                                        </p>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="flex items-center gap-2 text-sm text-foreground">
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                        {notification.schedule}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-foreground">
                                      {notification.dateRange}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                      <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                          notification.status,
                                        )}`}
                                      >
                                        {notification.status}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleCancelScheduled(notification.id)
                                        }
                                        className="text-red-600 hover:text-red-700 mr-2"
                                        disabled={
                                          isLoading ||
                                          cancelingIds.includes(notification.id)
                                        }
                                      >
                                        {cancelingIds.includes(
                                          notification.id,
                                        ) ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          "Cancel"
                                        )}
                                      </Button>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          handleViewDetails(notification)
                                        }
                                        className="text-primary hover:text-primary"
                                        disabled={isLoading}
                                      >
                                        <Eye className="h-4 w-4 mr-1" />
                                        View
                                      </Button>
                                    </td>
                                  </tr>
                                ),
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="px-6 py-12 text-center"
                                >
                                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                                  <p className="text-muted-foreground">
                                    No scheduled notifications found
                                  </p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {!isLoading && (
                          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
                            <p className="text-sm text-muted-foreground">
                              Showing {(scheduledPage - 1) * itemsPerPage + 1}{" "}
                              to{" "}
                              {Math.min(
                                scheduledPage * itemsPerPage,
                                filteredScheduledNotifications.length,
                              )}{" "}
                              of {filteredScheduledNotifications.length}{" "}
                              Notifications
                            </p>

                            <div className="flex items-center gap-2">
                              {/* Previous Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setScheduledPage((p) => Math.max(1, p - 1))
                                }
                                disabled={scheduledPage === 1 || isLoading}
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>

                              {/* Page Numbers */}
                              <div className="flex items-center gap-1">
                                {/* First Page */}
                                {scheduledTotalPages > 1 && (
                                  <Button
                                    variant={
                                      scheduledPage === 1
                                        ? "default"
                                        : "outline"
                                    }
                                    size="sm"
                                    onClick={() => setScheduledPage(1)}
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    1
                                  </Button>
                                )}

                                {/* Ellipsis before */}
                                {scheduledPage > 4 && (
                                  <span className="px-2 text-muted-foreground">
                                    ...
                                  </span>
                                )}

                                {/* 2 pages before */}
                                {scheduledPage > 3 &&
                                  Array.from(
                                    { length: 2 },
                                    (_, i) => scheduledPage - 3 + i,
                                  )
                                    .filter((p) => p > 1)
                                    .map((page) => (
                                      <Button
                                        key={page}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setScheduledPage(page)}
                                        className="w-10"
                                        disabled={isLoading}
                                      >
                                        {page}
                                      </Button>
                                    ))}

                                {/* 1 page before */}
                                {scheduledPage > 2 && scheduledPage - 2 > 1 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      setScheduledPage(scheduledPage - 2)
                                    }
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    {scheduledPage - 2}
                                  </Button>
                                )}

                                {/* 1 page before current */}
                                {scheduledPage > 1 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      setScheduledPage(scheduledPage - 1)
                                    }
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    {scheduledPage - 1}
                                  </Button>
                                )}

                                {/* Current Page */}
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="w-10"
                                  disabled
                                >
                                  {scheduledPage}
                                </Button>

                                {/* 1 page after */}
                                {scheduledPage < scheduledTotalPages && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      setScheduledPage(scheduledPage + 1)
                                    }
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    {scheduledPage + 1}
                                  </Button>
                                )}

                                {/* 2 pages after */}
                                {scheduledPage < scheduledTotalPages - 1 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      setScheduledPage(scheduledPage + 2)
                                    }
                                    className="w-10"
                                    disabled={isLoading}
                                  >
                                    {scheduledPage + 2}
                                  </Button>
                                )}

                                {/* Ellipsis after */}
                                {scheduledPage < scheduledTotalPages - 3 && (
                                  <span className="px-2 text-muted-foreground">
                                    ...
                                  </span>
                                )}

                                {/* Last Page */}
                                {scheduledTotalPages > 1 &&
                                  scheduledPage !== scheduledTotalPages && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        setScheduledPage(scheduledTotalPages)
                                      }
                                      className="w-10"
                                      disabled={isLoading}
                                    >
                                      {scheduledTotalPages}
                                    </Button>
                                  )}
                              </div>

                              {/* Next Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setScheduledPage((p) =>
                                    Math.min(scheduledTotalPages, p + 1),
                                  )
                                }
                                disabled={
                                  scheduledPage === scheduledTotalPages ||
                                  isLoading
                                }
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <NotificationSendModal
              isOpen={isSendModalOpen}
              onClose={() => {
                setIsSendModalOpen(false);
                setSendError(null);
              }}
              onSave={handleSendNotification}
              error={sendError}
              domainInfo={domainInfo}
              topics={topics}
              topicsLoading={topicsLoading}
              topicsError={topicsError}
            />

            <ScheduledNotificationModal
              isOpen={isScheduleModalOpen}
              onClose={() => setIsScheduleModalOpen(false)}
              onSave={handleScheduleNotification}
              domainInfo={domainInfo}
              topics={topics}
              topicsLoading={topicsLoading}
              topicsError={topicsError}
              user={user}
            />

            <NotificationDetailModal
              isOpen={isDetailModalOpen}
              onClose={() => {
                setIsDetailModalOpen(false);
                setSelectedNotification(null);
              }}
              notification={selectedNotification}
            />
          </div>
        </main>
      </div>
    </Layout>
  );
}
